import React, { useEffect, useState } from "react";
import { Dropdown, Form, Spinner as BSpinner } from "react-bootstrap";
import { getServices } from "../../services/Admin/adminServices";
import { debounce } from "lodash";

const ServiceSearchDropdown = ({ value, onSelect }) => {
  const [services, setServices] = useState([]);
  const [allServicesMap, setAllServicesMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  const [selectedServices, setSelectedServices] = useState([]);

  // Fetch initial batch of all services to map IDs to Titles
  useEffect(() => {
    const fetchAllMap = async () => {
      try {
        const res = await getServices(500, 0, "");
        const allList = res?.data?.data?.services || [];
        const map = {};
        allList.forEach((s) => {
          map[s._id] = s;
        });
        setAllServicesMap((prev) => ({ ...map, ...prev }));
      } catch (err) {
        console.error("Error building services map:", err);
      }
    };
    fetchAllMap();
  }, []);

  const fetchServicesData = async () => {
    try {
      setLoading(true);
      const response = await getServices(itemsPerPage, currentPage, debouncedSearch);
      const fetchedServices = response?.data?.data?.services || [];
      setServices(fetchedServices);
      setTotalItems(response?.data?.data?.count || 0);

      setAllServicesMap((prev) => {
        const updated = { ...prev };
        fetchedServices.forEach((s) => {
          updated[s._id] = s;
        });
        return updated;
      });
    } catch (error) {
      console.error("Error fetching services in dropdown:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServicesData();
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(search);
      setCurrentPage(0);
    }, 500);
    handler();
    return () => handler.cancel();
  }, [search]);

  // Sync selectedServices with value prop and allServicesMap
  useEffect(() => {
    let normalized = [];
    if (Array.isArray(value)) {
      normalized = value;
    } else if (value) {
      normalized = [value];
    }

    const resolved = normalized
      .map((item) => {
        const id = typeof item === "object" ? item._id : item;
        const title = typeof item === "object" ? item.title : null;

        if (allServicesMap[id]) {
          return allServicesMap[id];
        }
        const foundInCurrent = services.find((s) => s._id === id);
        if (foundInCurrent) {
          return foundInCurrent;
        }
        if (title && title !== id) {
          return { _id: id, title };
        }
        // Fallback placeholder object until map loads
        return { _id: id, title: "Loading..." };
      })
      .filter(Boolean);

    setSelectedServices(resolved);
  }, [value, services, allServicesMap]);

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const isSelected = (serviceId) => {
    return selectedServices.some((s) => s._id === serviceId);
  };

  const handleToggleService = (service) => {
    let newSelected;
    if (isSelected(service._id)) {
      newSelected = selectedServices.filter((s) => s._id !== service._id);
    } else {
      newSelected = [...selectedServices, service];
    }
    setSelectedServices(newSelected);
    if (onSelect) {
      onSelect(newSelected);
    }
  };

  const getToggleButtonLabel = () => {
    if (selectedServices.length === 0) return "Select Services...";
    return selectedServices
      .map((s) => (s.title && s.title !== s._id ? s.title : "Loading..."))
      .filter((t) => t !== "Loading...")
      .join(", ") || "Select Services...";
  };

  return (
    <Dropdown className="w-100">
      <Dropdown.Toggle
        variant="outline-secondary"
        className="w-100 d-flex justify-content-between align-items-center"
        style={{ borderColor: "#0AB7EA", borderRadius: "0.5rem", background: "#fff", color: "#333" }}
      >
        <span className="text-truncate me-2">{getToggleButtonLabel()}</span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-100 p-2 shadow" style={{ maxHeight: "350px", overflowY: "auto" }}>
        <div className="mb-2" onClick={(e) => e.stopPropagation()}>
          <Form.Control
            type="text"
            placeholder="Search service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ borderRadius: "0.4rem", borderColor: "#0AB7EA" }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>

        {selectedServices.length > 0 && (
          <div
            className="d-flex flex-wrap gap-1 mb-2 p-1 border rounded bg-light"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: "70px", overflowY: "auto" }}
          >
            {selectedServices.map((s) => {
              const displayTitle = s.title && s.title !== s._id ? s.title : "...";
              return (
                <span
                  key={s._id}
                  className="badge d-flex align-items-center gap-1"
                  style={{ backgroundColor: "#0AB7EA", color: "#fff", fontSize: "11px", fontWeight: "normal" }}
                >
                  {displayTitle}
                  <span
                    style={{ cursor: "pointer", marginLeft: "4px", fontWeight: "bold" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleService(s);
                    }}
                  >
                    &times;
                  </span>
                </span>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="text-center p-3">
            <BSpinner animation="border" size="sm" style={{ color: "#0AB7EA" }} />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center p-2 text-muted">No services found</div>
        ) : (
          <div style={{ maxHeight: "180px", overflowY: "auto" }}>
            {services.map((s) => {
              const checked = isSelected(s._id);
              return (
                <Dropdown.Item
                  key={s._id}
                  active={checked}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleService(s);
                  }}
                  className="d-flex align-items-center justify-content-between py-2"
                >
                  <div className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      checked={checked}
                      onChange={() => {}}
                      className="me-2"
                      style={{ pointerEvents: "none" }}
                    />
                    <span>{s.title}</span>
                  </div>
                  {s.phoneNumber && <small className="text-muted ms-2">{s.phoneNumber}</small>}
                </Dropdown.Item>
              );
            })}
          </div>
        )}

        <Dropdown.Divider />

        <div className="d-flex justify-content-between align-items-center px-2 py-1" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage === 0 || loading}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentPage((prev) => Math.max(prev - 1, 0));
            }}
          >
            &laquo; Prev
          </button>
          <span style={{ fontSize: "12px", color: "#6c757d" }}>
            {currentPage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage >= totalPages - 1 || loading}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
            }}
          >
            Next &raquo;
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ServiceSearchDropdown;
